import {
  createApiKeysApi,
  revokeApiKeysApi,
  deleteApiKeysApi,
  editApiKeysApi,
  listApiKeysApi
} from '@/api/apiKey'
import type { V1alpha1APIKey } from '@streamnative/cloud-api-client-typescript'
import { promiseTimeout } from '@vueuse/core'
import * as jose from 'jose'
import { groupBy } from 'lodash-es'
import type { PulsarState } from './usePulsarState'
import { isPulsarInstance } from './useInstance'
import { getApiKeyStatus } from '@/utils/apiKey'

let lastOrg: string | undefined = undefined

const latestCreatedApiKeyToken = ref<string>('')

const apiKeys = ref<V1alpha1APIKey[]>([])
const apiKeysGrouped = computed(() => {
  const { instanceNames } = useInstance()
  const { isAuthV2 } = useOrganization()
  return groupBy(
    apiKeys.value
      .filter(
        apiKey =>
          isAuthV2.value ||
          (apiKey.spec?.instanceName && instanceNames.value.includes(apiKey.spec?.instanceName))
      )
      .map(apiKey => {
        return {
          id: apiKey.status?.keyId,
          encryptedToken: apiKey.status?.encryptedToken?.jwe,
          token: apiKey.status?.token,
          name: apiKey.metadata?.name,
          description: apiKey.spec?.description,
          instanceName: apiKey.spec?.instanceName,
          serviceAccountName: apiKey.spec?.serviceAccountName,
          issuedAt: apiKey.status?.issuedAt || apiKey.metadata?.creationTimestamp,
          expiresAt: apiKey.spec?.expirationTime ?? 'Never Expires',
          revokedAt: apiKey.status?.revokedAt,
          status: getApiKeyStatus({
            isDeleting: Boolean(apiKey.metadata?.deletionTimestamp),
            isRevoked:
              apiKey.status?.conditions?.some(c => {
                return c.type === 'Revoked' && c.status === 'True'
              }) ?? false,
            isExpired:
              apiKey.status?.conditions?.some(c => {
                return c.type === 'Expired' && c.status === 'True'
              }) ?? false,
            isIssued: Boolean(
              apiKey.status?.conditions?.some(c => {
                return c.type === 'Issued' && c.status === 'True'
              }) && apiKey.status.keyId
            )
          })
        }
      }),
    item => item.serviceAccountName
  )
})

const getApiKeys = async (organization?: string | undefined) => {
  if (!organization) {
    organization = usePulsarState().mustOrganization()
  }
  const res = await listApiKeysApi(organization)
  apiKeys.value = res.items
  return apiKeys.value
}

function ab2str(buf: ArrayBuffer) {
  return String.fromCharCode.apply(null, [...new Uint8Array(buf)])
}

function str2ab(str: string) {
  const buf = new ArrayBuffer(str.length)
  const bufView = new Uint8Array(buf)
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i)
  }
  return buf
}

async function exportCryptoKey(key: CryptoKey) {
  const exported = await window.crypto.subtle.exportKey('spki', key)
  const exportedAsString = ab2str(exported)
  const exportedAsBase64 = window.btoa(exportedAsString)
  const pemExported = `-----BEGIN PUBLIC KEY-----\n${exportedAsBase64}\n-----END PUBLIC KEY-----`
  return pemExported
}

const createKeyPair = async () => {
  return await window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-1'
    },
    true,
    ['encrypt', 'decrypt']
  )
}

function decryptKey(privateKey: CryptoKey, ciphertext: BufferSource) {
  return window.crypto.subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, ciphertext)
}

const decryptToken = async (privateKey: CryptoKey, encryptedToken: string, isJwe: boolean) => {
  if (isJwe) {
    const { plaintext } = await jose.compactDecrypt(encryptedToken, privateKey)
    return new TextDecoder().decode(plaintext)
  }
  let decryptedToken = str2ab(window.atob(encryptedToken))
  decryptedToken = await decryptKey(privateKey, decryptedToken)
  return ab2str(decryptedToken)
}

const getLatestCreatedApiKeyToken = async (
  name: string,
  serviceAccountName: string,
  retryCount: number,
  keyPair: CryptoKeyPair
) => {
  const _apiKeys = await getApiKeys()
  const encryptedToken = _apiKeys.find(
    ak => ak.spec?.serviceAccountName === serviceAccountName && ak.metadata?.name === name
  )?.status?.encryptedToken?.jwe
  if (encryptedToken) {
    const token = await decryptToken(keyPair.privateKey, encryptedToken, true)
    latestCreatedApiKeyToken.value = token
  }
  if (!latestCreatedApiKeyToken.value && retryCount > 0) {
    await promiseTimeout(2000) // sleep to wait 2s
    await getLatestCreatedApiKeyToken(name, serviceAccountName, retryCount - 1, keyPair)
  }
}

export const createApiKey = async (
  {
    name,
    instanceName,
    serviceAccountName,
    description,
    expirationTime
  }: {
    name: string
    instanceName?: string
    serviceAccountName: string
    description: string
    expirationTime: string
  },
  organization?: string
) => {
  latestCreatedApiKeyToken.value = ''
  if (!organization) {
    organization = usePulsarState().mustOrganization()
  }
  const { isAuthV2, isInstanceEnabled } = useOrganization()
  const { instances } = useInstance()
  const isOrgScopedApiKey = isAuthV2.value && isInstanceEnabled.value
  const targetInstance = instanceName
    ? instances.value.find(ins => ins.metadata?.name === instanceName)
    : undefined

  const annotations: Record<string, string> | undefined =
    isOrgScopedApiKey || (targetInstance && !isPulsarInstance(targetInstance))
      ? { 'cloud.streamnative.io/auth': 'v2' }
      : undefined

  const keyPair = await createKeyPair()
  const encryptionKey = await exportCryptoKey(keyPair.publicKey)
  const { status } = await createApiKeysApi(organization, {
    name,
    ...(instanceName ? { instanceName } : {}),
    serviceAccountName,
    description,
    expirationTime,
    encryptionKey: {
      pem: encryptionKey
    },
    annotations
  })
  if (status?.encryptedToken?.jwe) {
    const token = await decryptToken(keyPair.privateKey, status?.encryptedToken?.jwe, true)
    latestCreatedApiKeyToken.value = token
  } else {
    await getLatestCreatedApiKeyToken(name, serviceAccountName, 3, keyPair)
  }
}

export const revokeApiKey = async (apiKeyName: string, organization?: string) => {
  if (!organization) {
    organization = usePulsarState().mustOrganization()
  }
  await revokeApiKeysApi(apiKeyName, organization)
}

export const deleteApiKey = async (apiKeyName: string, organization?: string) => {
  if (!organization) {
    organization = usePulsarState().mustOrganization()
  }
  await deleteApiKeysApi(apiKeyName, organization)
}

export const editApiKey = async (
  apiKeyName: string,
  {
    description
  }: {
    description?: string
  },
  organization?: string
) => {
  if (!organization) {
    organization = usePulsarState().mustOrganization()
  }
  await editApiKeysApi(apiKeyName, organization, { description })
}

export const init = (initialState: PulsarState) => {
  const { isRbacUpdating } = rbacHelper()

  const valueChanged = async ([org, ab]: [string | undefined, boolean | undefined]) => {
    if (!org) {
      apiKeys.value = []
      lastOrg = undefined
      return
    }
    if (ab) {
      return
    }

    if (org !== lastOrg) {
      const { canDescribeApiKeyList } = rbacManager()
      if (canDescribeApiKeyList()) {
        await getApiKeys(org)
      }
    }
    lastOrg = org
  }

  return valueChanged([initialState.organization, isRbacUpdating.value])
}

export const useApiKey = () => {
  return {
    init,
    createApiKey,
    editApiKey,
    revokeApiKey,
    deleteApiKey,
    apiKeysGrouped,
    getApiKeys,
    apiKeys,
    latestCreatedApiKeyToken
  }
}
